import { getHours, getMinutes, parse } from 'date-fns';
import { InfoIcon } from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Container, DropdownOption } from '@/components/chariot-ui';
import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useScanSchedule } from '@/hooks/useScanSchedule';
import { useUpdateSetting } from '@/hooks/useSetting';
import type { CadenceFrequency } from '@/types';
import { WeekDay, WeekDayOptions } from '@/types';
import { cn } from '@/utils/classname';
import {
  convertDayToUTC,
  convertToUTC,
  getCurrentTimeZone,
} from '@/utils/date.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * The value mirrored to a parent (the wizard) via `onChange` when `embedded`.
 * Times are the user-facing (selected-timezone) values, matching internal state.
 */
export interface ScanScheduleValue {
  days: WeekDay[];
  start?: string; // "HH:MM"
  end?: string; // "HH:MM"
  manualBypass: boolean;
  timezone: DropdownOption;
}

/** The UTC payload `submit()` actually wrote — lets the wizard derive cadence. */
export interface ScanScheduleSubmitResult {
  days: WeekDay[]; // UTC-converted weekdays
  start: string; // UTC "HH:MM"
  end: string; // UTC "HH:MM"
}

export interface ScanScheduleHandle {
  /**
   * Computes the final UTC `{days, start, end}` from current state WITHOUT
   * writing anything. Returns null when invalid. Lets the wizard derive the
   * cadence payload and write cadence FIRST (OPEN-Q-3 order) using the same
   * tz-conversion source of truth as the schedule write.
   */
  computeUtc: () => ScanScheduleSubmitResult | null;
  /** Writes `schedule` (silent). Returns the UTC payload on success, null on failure/invalid. */
  submit: () => Promise<ScanScheduleSubmitResult | null>;
}

export interface ScanScheduleProps {
  customerEmail?: string;
  onClose?: () => void;
  /** When true: no <h1>, no internal Save/Cancel/Reset buttons (wizard owns chrome). */
  embedded?: boolean;
  /** Fires when (start && end && timezone && days.length > 0) validity changes. */
  onValidityChange?: (valid: boolean) => void;
  /** Mirrors internal state to the wizard for the live summary band. */
  onChange?: (value: ScanScheduleValue) => void;
  /** Drives Step-2 label/help/tag swaps (FIDELITY F3/F4/F5). */
  frequency?: CadenceFrequency;
  /** Controlled manual-bypass value for embedded (wizard) mode; the wizard owns
   *  this state and renders the toggle in its own step. Ignored when not embedded. */
  manualBypass?: boolean;
}

const parseTime = (timeString: string): { hour: number; minute: number } => {
  const [hour, minute] = timeString.split(':').map(Number);
  return { hour, minute };
};

const isWeeklyish = (f?: CadenceFrequency) =>
  f === undefined || f === 'weekly' || f === 'biweekly';

export const ScanSchedule = forwardRef<ScanScheduleHandle, ScanScheduleProps>(
  function ScanSchedule(
    {
      customerEmail,
      onClose,
      embedded,
      onValidityChange,
      onChange,
      frequency,
      manualBypass: controlledManualBypass,
    },
    ref
  ) {
    const scanTimeOptions = useMemo(() => {
      const options = [];

      [...Array(12).keys()].forEach(i => {
        const hour = i <= 9 ? `0${i}` : `${i}`;
        options.push({ label: `${hour}:00 AM`, value: `${hour}:00` });
      });

      options.push({ label: '12:00 PM', value: '12:00' });

      [...Array(11).keys()].forEach(i => {
        const hour = i < 9 ? `0${i + 1}` : `${i + 1}`;
        options.push({ label: `${hour}:00 PM`, value: `${13 + i}:00` });
      });

      return options;
    }, []);
    const { offset, tz } = getCurrentTimeZone();
    const {
      currentScanSchedule,
      isUpdatingScanSchedule,
      updateScanScheduleAsync,
      deleteScanSchedule,
    } = useScanSchedule({ email: customerEmail });
    // Silent writer for embedded mode (the wizard owns the combined toast).
    const { mutateAsync: updateSettingSilent } =
      useUpdateSetting(customerEmail);
    const timezoneOptions = [
      {
        label: '(GMT+00:00) UTC',
        value: 'UTC',
      },
      {
        label: `(${offset}) ${tz}`,
        value: tz,
      },
    ];

    const [scanDays, setScanDays] = useState<WeekDay[]>(
      currentScanSchedule?.days || []
    );
    const [start, setStart] = useState<DropdownOption | undefined>(
      scanTimeOptions.find(option => option.value === currentScanSchedule.start)
    );
    const [end, setEnd] = useState<DropdownOption | undefined>(
      scanTimeOptions.find(option => option.value === currentScanSchedule.end)
    );
    const [savedTimezone, setSavedTimezone] = useStorage<DropdownOption>(
      { key: StorageKey.SCAN_SCHEDULE_TIMEZONE },
      timezoneOptions[0] // Default timezone is UTC
    );
    const [timezone, setTimezone] = useState<DropdownOption>(savedTimezone);
    const [manualBypass, setManualBypass] = useState<boolean>(
      currentScanSchedule?.manualBypass ?? false
    );

    // `useScanSchedule` rebuilds `currentScanSchedule` (and its `.days` array)
    // with a fresh reference on every render, so this effect's dep changes
    // every render. Guard on the SERIALIZED content of the fields we sync so we
    // only push API data into local state when it actually changes — otherwise
    // setScanDays/setStart/etc. fire a fresh `[]`/option every render and create
    // an infinite render loop (child setState → parent re-render → new
    // `currentScanSchedule` ref → effect re-fires).
    const lastSyncedSchedule = useRef<string | null>(null);

    // Sync state with API data when it arrives
    useEffect(() => {
      if (!currentScanSchedule) return;

      const serialized = JSON.stringify({
        days: currentScanSchedule.days || [],
        start: currentScanSchedule.start,
        end: currentScanSchedule.end,
        manualBypass: currentScanSchedule.manualBypass ?? false,
      });
      if (serialized === lastSyncedSchedule.current) return;
      lastSyncedSchedule.current = serialized;

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScanDays(currentScanSchedule.days || []);

      // The hook already converts the schedule times to HH:mm format
      const startOption = scanTimeOptions.find(
        option => option.value === currentScanSchedule.start
      );
      const endOption = scanTimeOptions.find(
        option => option.value === currentScanSchedule.end
      );

      if (startOption) setStart(startOption);
      if (endOption) setEnd(endOption);
      setManualBypass(currentScanSchedule.manualBypass ?? false);
    }, [currentScanSchedule, scanTimeOptions]);

    const isValid = !!start && !!end && !!timezone && scanDays.length > 0;

    // Notify the wizard of validity changes (F2/I4 gate the Save button).
    // Guard on the actual boolean so we only emit when validity flips — emitting
    // on every render would let the parent's setState feed back into our own
    // re-render and loop.
    const lastEmittedValidity = useRef<boolean | null>(null);
    useEffect(() => {
      if (isValid === lastEmittedValidity.current) return;
      lastEmittedValidity.current = isValid;
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    // Mirror state to the wizard for the live summary band (C4). This effect
    // builds a FRESH object literal, so it must emit only when the emitted
    // VALUE actually changes — otherwise the parent's setState re-renders us,
    // re-fires this effect, and loops. Guard on the serialized value.
    const lastEmittedValue = useRef<string | null>(null);
    useEffect(() => {
      const value: ScanScheduleValue = {
        days: scanDays,
        start: start?.value as string | undefined,
        end: end?.value as string | undefined,
        manualBypass,
        timezone,
      };
      const serialized = JSON.stringify(value);
      if (serialized === lastEmittedValue.current) return;
      lastEmittedValue.current = serialized;
      onChange?.(value);
    }, [scanDays, start, end, manualBypass, timezone, onChange]);

    function getTimeOptions(type: 'start' | 'end') {
      const options = scanTimeOptions;

      if (isUpdatingScanSchedule) {
        return options.map(currentOption => ({
          ...currentOption,
          disabled: true,
        }));
      }

      if (type === 'start') {
        return options.map(option => ({
          ...option,
          disabled: option.value === start?.value,
        }));
      }
      if (!start) {
        return options.map(option => ({ ...option, disabled: false }));
      }

      // Parse start time
      const startTime = parse(start.value as string, 'HH:mm', new Date());

      // Convert time to minutes since the start of the day
      const toMinutes = (time: Date) => getHours(time) * 60 + getMinutes(time);
      const startMinutes = toMinutes(startTime);
      const minGap = 360; // 6 hours in minutes
      const fullDay = 1440; // 24 hours in minutes

      return options.map(option => {
        const optionTime = parse(option.value, 'HH:mm', new Date());
        const optionMinutes = toMinutes(optionTime);

        const isStartDisabled = option.value === start.value;

        // Disable any time within 6 hours after start time
        let isWithinRestrictedRange = false;

        if (startMinutes + minGap < fullDay) {
          // Normal case: Start time and valid range are within the same day
          isWithinRestrictedRange =
            optionMinutes > startMinutes &&
            optionMinutes < startMinutes + minGap;
        } else {
          // Wrap-around case: Start time is late at night, and end time might wrap into the next day
          const cutoffTime = (startMinutes + minGap) % fullDay;
          isWithinRestrictedRange =
            optionMinutes > startMinutes || optionMinutes < cutoffTime;
        }

        return {
          ...option,
          disabled: isStartDisabled || isWithinRestrictedRange,
        };
      });
    }

    const startItems = getTimeOptions('start').map(o => (
      <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </SelectItem>
    ));
    const endItems = getTimeOptions('end').map(o => (
      <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </SelectItem>
    ));

    useEffect(() => {
      if (!start && !end && scanDays.length > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setStart({ label: '12:00 AM', value: '00:00' });
        setEnd({ label: '11:00 PM', value: '23:00' });
      }
    }, [start, end, scanDays]);

    /**
     * Computes the final UTC `{days, start, end}` from current state, applying
     * the exact same tz conversion the standalone Save path uses
     * (convertToUTC / convertDayToUTC). Single source of truth for both the
     * `schedule` write AND the wizard's cadence-payload derivation (§8).
     */
    function computeUtcPayload(): ScanScheduleSubmitResult | null {
      if (!start?.value || !end?.value || !timezone || scanDays.length === 0) {
        return null;
      }
      if (timezone.value !== 'UTC') {
        const { utcTime: utcStartTime, diff: diffStart } = convertToUTC(
          start.value as string,
          timezone.value as string
        );
        const { utcTime: utcEndTime } = convertToUTC(
          end.value as string,
          timezone.value as string
        );
        const days = scanDays.map(day => convertDayToUTC(day, diffStart));
        return { days, start: utcStartTime, end: utcEndTime };
      }
      return {
        days: scanDays,
        start: start.value as string,
        end: end.value as string,
      };
    }

    // Imperative handle for the wizard: write `schedule` silently and return
    // the UTC payload that was written (G4-equivalent shape).
    useImperativeHandle(
      ref,
      () => ({
        computeUtc: () => computeUtcPayload(),
        submit: async () => {
          const payload = computeUtcPayload();
          if (!payload) return null;
          // Persist the selected timezone once, on actual save (kept out of the
          // pure computeUtc() path so it isn't written twice per save).
          setSavedTimezone(timezone);
          // Byte-for-byte equivalent to useScanSchedule's update payload (G4),
          // but silent so the wizard owns the single combined toast (OPEN-Q-3).
          // Honor the documented contract: return null on write failure so the
          // wizard's partial-failure branch (cadence saved, schedule failed)
          // fires instead of an unhandled rejection escaping handleSaveSchedule.
          try {
            await updateSettingSilent({
              name: 'schedule',
              value: {
                days: payload.days,
                start: parseTime(payload.start),
                end: parseTime(payload.end),
                manualBypass: embedded
                  ? (controlledManualBypass ?? false)
                  : manualBypass,
              },
              silent: true,
            });
          } catch {
            return null;
          }
          return payload;
        },
      }),
      // The handle reads current state; recreate when those values change.

      [start, end, timezone, scanDays, manualBypass, controlledManualBypass]
    );

    const weekdayLabel = isWeeklyish(frequency)
      ? 'Days to scan'
      : 'Eligible launch days';

    const weekdayHelp = (() => {
      switch (frequency) {
        case 'biweekly':
          return 'Pick the day(s) of the week scans may launch. They recur on this rhythm every 2 weeks (the starting week is anchored on save).';
        case 'monthly':
          return "These weekdays don't add more scans — they only choose which window opens the scan. Each month, the first matching window fires once.";
        case 'quarterly':
          return 'These weekdays only choose which window opens the scan. Each quarter (Jan · Apr · Jul · Oct), the first matching window fires once.';
        case 'weekly':
          return 'Pick the day(s) of the week scans may launch.';
        default:
          return '';
      }
    })();

    const showAnchorFrame = !isWeeklyish(frequency);
    const anchorTag =
      frequency === 'quarterly'
        ? 'First eligible window per quarter'
        : 'First eligible window per month';

    // Width/layout trigger: the responsive full-width layout (built for the
    // customer-impersonation modal) must ALSO apply in the embedded wizard,
    // whose dialog is only 720px wide. Without this, a Praetorian operator
    // (customerEmail undefined) falls back to the fixed wide layout and the
    // 7×108px weekday row overflows the dialog. Visibility conditions (e.g. the
    // manual-bypass row's `!customerEmail`) are intentionally NOT folded in here.
    const fitContainer = embedded || !!customerEmail;

    const weekdaySection = (
      <div
        role="group"
        aria-label={weekdayLabel}
        className="grid grid-cols-7 gap-2 pb-4"
      >
        {WeekDayOptions.map(({ label, value }) => {
          const selected = scanDays.includes(value);
          return (
            <button
              key={label}
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label={label}
              disabled={isUpdatingScanSchedule}
              onClick={() =>
                setScanDays(prev =>
                  prev.includes(value)
                    ? prev.filter(d => d !== value)
                    : [...prev, value]
                )
              }
              data-testid={`scan-schedule-day-checkbox-${value}`}
              className={cn(
                'flex h-9 items-center justify-center rounded-md border text-sm font-medium transition-colors',
                'focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-hidden',
                selected
                  ? 'border-chariot-brand-primary bg-chariot-brand-primary text-white'
                  : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:bg-chariot-background-secondary',
                isUpdatingScanSchedule && 'cursor-not-allowed opacity-50'
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    );

    return (
      <div className="flex flex-col" data-testid="scan-schedule-form">
        {!embedded && <h1 className="text-2xl font-bold">Scan Schedule</h1>}
        {!embedded && (
          <p>
            Select a default scan schedule that will be{' '}
            <span className="font-bold italic">applied to all assets</span>.
            This determines how often Chariot scans your assets.
          </p>
        )}
        <Container
          className={cn(
            'bg-chariot-background-primary my-4 w-fit px-6 py-4',
            fitContainer && 'w-full'
          )}
        >
          {/* Days to scan — label/help swap by cadence frequency (F3/F4/F5) */}
          {embedded ? (
            <>
              <h2 className="text-md pb-1 font-bold">{weekdayLabel}</h2>
              {weekdayHelp && (
                <p className="text-chariot-text-secondary pb-2 text-xs">
                  {weekdayHelp}
                </p>
              )}
              {showAnchorFrame ? (
                <div className="border-chariot-brand-primary mb-2 border-l-[3px] pl-3">
                  <div className="text-chariot-brand-primary mb-1.5 text-xs font-bold tracking-wide uppercase">
                    {anchorTag}
                  </div>
                  {weekdaySection}
                </div>
              ) : (
                weekdaySection
              )}
            </>
          ) : (
            <>
              <h2 className="text-md pb-1 font-bold">Days to Scan Assets</h2>
              {weekdaySection}
            </>
          )}
          <div className="flex items-center gap-2">
            <h2 className="text-md pb-1 font-bold">Choose a Time</h2>
            <Tooltip title="Requires a minimum scan window of 6 hours">
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          </div>
          {embedded && (
            <p className="text-chariot-text-secondary pb-2 text-xs">
              Scans may only start while this window is open. The hourly cron
              fires at the window&rsquo;s opening.
            </p>
          )}
          <div
            className={cn('flex gap-10', fitContainer && 'flex-wrap gap-y-4')}
          >
            <div className="flex items-center gap-4">
              <div className="w-44">
                <Select
                  value={(start?.value as string | undefined) ?? ''}
                  onValueChange={v => {
                    setStart(scanTimeOptions.find(o => o.value === v));
                    setEnd(undefined);
                  }}
                  disabled={isUpdatingScanSchedule}
                >
                  <SelectTrigger
                    aria-label="Scan window start time"
                    data-testid="scan-schedule-start-time-select"
                  >
                    <SelectValue placeholder="Select start time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))]">
                    {startItems}
                  </SelectContent>
                </Select>
              </div>
              <p className="font-bold">to</p>
              <div className="w-44">
                <Select
                  value={(end?.value as string | undefined) ?? ''}
                  onValueChange={v =>
                    setEnd(scanTimeOptions.find(o => o.value === v))
                  }
                  disabled={isUpdatingScanSchedule || !start}
                >
                  <SelectTrigger
                    aria-label="Scan window end time"
                    data-testid="scan-schedule-end-time-select"
                  >
                    <SelectValue placeholder="Select end time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))]">
                    {endItems}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <h2 className="text-md font-bold">Timezone</h2>
              <div className="w-56">
                <Select
                  value={(timezone?.value as string | undefined) ?? ''}
                  onValueChange={v =>
                    setTimezone(
                      timezoneOptions.find(o => o.value === v) ??
                        timezoneOptions[0]
                    )
                  }
                  disabled={isUpdatingScanSchedule}
                >
                  <SelectTrigger
                    aria-label="Scan window timezone"
                    data-testid="scan-schedule-timezone-select"
                  >
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))]">
                    {timezoneOptions
                      // The local-tz option collapses onto the UTC option for
                      // UTC-region users; dedupe by value so Radix gets unique
                      // item values (and React unique keys).
                      .filter(
                        (o, i, arr) =>
                          arr.findIndex(x => x.value === o.value) === i
                      )
                      .map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {embedded && (
            <p className="text-chariot-text-secondary mt-2.5 flex items-start gap-2 text-xs leading-relaxed">
              <InfoIcon className="text-chariot-status-info mt-px size-3.5 shrink-0" />
              <span>
                Times are stored and shown in{' '}
                <b className="text-chariot-text-primary font-bold">UTC</b>. A
                minimum{' '}
                <b className="text-chariot-text-primary font-bold">6-hour</b>{' '}
                window is required. Choosing a non-UTC timezone converts to UTC
                on save.
              </span>
            </p>
          )}
          {/* Manual-bypass row: hidden when a customer is being edited (parity)
              and when embedded — the wizard renders this toggle in its own
              Step 3 and owns the value via the `manualBypass` prop. */}
          {!customerEmail && !embedded && (
            <>
              <div className="border-chariot-stroke my-4 border-t" />
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h2 className="text-md font-bold">
                    {embedded
                      ? 'Manual scans bypass the schedule'
                      : 'Manual scans bypass schedule'}
                  </h2>
                  <p className="text-chariot-text-secondary max-w-[46ch] text-xs leading-relaxed">
                    {embedded
                      ? 'When enabled, manually triggered scans run immediately, ignoring both the cadence period and the time window.'
                      : 'When enabled, manually triggered scans will run immediately regardless of the scan schedule.'}
                  </p>
                </div>
                <ToggleSwitch
                  checked={manualBypass}
                  onChange={setManualBypass}
                  disabled={isUpdatingScanSchedule}
                  ariaLabel="Toggle manual scan bypass"
                  data-testid="scan-schedule-manual-bypass-toggle"
                />
              </div>
            </>
          )}
        </Container>

        {!embedded && (
          <div className={cn('flex w-fit gap-2', customerEmail && 'ml-auto')}>
            {customerEmail && (
              <Button
                variant="outline"
                onClick={async () => {
                  onClose?.();
                }}
                data-testid="scan-schedule-cancel-button"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={async () => {
                if (
                  start?.value &&
                  end?.value &&
                  timezone &&
                  scanDays.length > 0
                ) {
                  setSavedTimezone(timezone);
                  if (timezone.value !== 'UTC') {
                    const { utcTime: utcStartTime, diff: diffStart } =
                      convertToUTC(
                        start.value as string,
                        timezone.value as string
                      );
                    const { utcTime: utcEndTime } = convertToUTC(
                      end.value as string,
                      timezone.value as string
                    );
                    // convert days to utc days
                    const days = scanDays.map(day => {
                      return convertDayToUTC(day, diffStart);
                    });
                    await updateScanScheduleAsync({
                      days,
                      start: utcStartTime,
                      end: utcEndTime,
                      manualBypass,
                    });
                  } else {
                    await updateScanScheduleAsync({
                      days: scanDays,
                      start: start.value as string,
                      end: end.value as string,
                      manualBypass,
                    });
                  }
                  onClose?.();
                }
              }}
              variant="default"
              className="w-fit"
              disabled={!start || !end || !timezone || scanDays.length === 0}
              data-testid="scan-schedule-save-button"
            >
              Save
            </Button>
            {!customerEmail && (
              <Button
                variant="outline"
                onClick={async () => {
                  await deleteScanSchedule();
                  setScanDays([]);
                  setStart(undefined);
                  setEnd(undefined);
                  setTimezone(timezoneOptions[0]);
                }}
                data-testid="scan-schedule-reset-button"
              >
                Reset to Default
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);
